import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import axios from 'axios';
import { useFormikContext } from 'formik';
import _ from 'lodash';

import { useStyles } from '~/components/../assets/styles';
import AutocompleteFormik from '~/components/AutocompleteFormik';
import CardDialog from '~/components/CardDialog';
import mixpanel from '~/components/CmsMain/mixpanel';
import { ConfirmModal } from '~/components/ConfirmModal';
import { Text } from '~/components/core';
import AlertBanner from '~/components/core/AlertBanner';
import Grid from '~/components/core/Atomic/Grid/Grid';
import MenuItem from '~/components/core/Atomic/MenuItem';
import Typography from '~/components/core/Atomic/Typography';
import { Card } from '~/components/core/Cards/Card';
import DialogFooterActions from '~/components/core/DialogFooterActions';
import TextAreaWithTokens from '~/components/core/Formik/TextAreaWithTokens';
import { WarningIcon } from '~/components/icons';
import RadioButtonFormik from '~/components/RadioButtonFormik';
import { isEmptyConditionsTree } from '~/components/SystemConfiguration/AutomaticRulesConfiguration/Conditions/QueryBuilder';
import { ConditionsFormik } from '~/components/SystemConfiguration/AutomaticRulesConfiguration/Notifications/NotificationRuleDialog/ConditionsFormik';
import { EventScheduleSelect } from '~/components/SystemConfiguration/AutomaticRulesConfiguration/Notifications/NotificationRuleDialog/EventScheduleSelect';
import styles from '~/components/SystemConfiguration/AutomaticRulesConfiguration/Notifications/NotificationRuleDialog/notificationDialogInner.module.scss';
import NotificationConditionsQueryBuilderFormik from '~/components/SystemConfiguration/AutomaticRulesConfiguration/Notifications/NotificationRuleDialog/QueryBuilder/NotificationConditionsQueryBuilderFormik';
import RuleNotificationConfigurationFieldsFormik from '~/components/SystemConfiguration/AutomaticRulesConfiguration/Notifications/NotificationRuleDialog/RuleNotificationConfigurationFieldsFormik';
import { TimeBasedConditionFormik } from '~/components/SystemConfiguration/AutomaticRulesConfiguration/Notifications/NotificationRuleDialog/TimeBasedConditionFormik';
import {
  ALL_OPTION,
  shouldUseConditionsQueryBuilder,
  useNotificationConfigurationTool,
} from '~/components/SystemConfiguration/AutomaticRulesConfiguration/Notifications/utils';
import PriorityCategoryFieldsFormikInner from '~/components/SystemConfiguration/NotificationUtils/PriorityCategoryFIeldsFormikInner';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import { useOrganizationUnits } from '~/components/SystemConfiguration/Users/hooks/useOrganizationUnits';
import {
  MultiSelectTextFieldFormik,
  TextFieldFormik,
  useAllOptionOnMultiSelect,
  useSetDefaultFieldsOnChange,
} from '~/components/TextFieldFormik';
import LobCell from '~/components/TPA/LOB/LobCell';
import LobRadioFormik from '~/components/TPA/LOB/LobRadioFormik';
import { NOTIFICATION_MIXPANEL_EVENT_SOURCES, NOTIFICATION_MIXPANEL_EVENTS } from '~/pocs/mixpanel';
import { CONFIGURATION_FEATURES_NAMES, NOTIFICATIONS_ENTITY_STATUSES, NOTIFICATIONS_SCOPES } from '~/Types';
import { isFeatureEnabled, reportAxiosError } from '~/Utils';
import cn from '~/Utils/cn';

export const NotificationDialogInner = ({
  rule,
  onClose,
  subOrgIdToName,
  events,
  conditions,
  onConditionFieldsLoaded,
}) => {
  const { classes } = useStyles();
  const { isSubmitting, handleSubmit, values, setFieldValue, setValues } = useFormikContext();
  const {
    organization,
    organizationOperationalDetails: { notificationUsableTokensMap },
  } = useSysconfig();
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [eventTypeOptions, setEventTypeOptions] = useState([]);
  const [selectedEventConfig, setSelectedEventConfig] = useState({});
  const [relevantTokens, setRelevantTokens] = useState({});
  const [scopeTypesOptions, setScopeTypesOptions] = useState({ claim: [], exposure: [] });
  const [scopeSubTypesOptions, setScopeSubTypesOptions] = useState({});
  const [pendingTriggerTypeChange, setPendingTriggerTypeChange] = useState(null);
  // Blocks Save while the QueryBuilder's field catalog is in flight, so a rule
  // can't be submitted against a registry the form hasn't validated against yet.
  const [isConditionFieldsLoading, setIsConditionFieldsLoading] = useState(false);
  const { recipientTypes } = useNotificationConfigurationTool();

  const { isUnitsLoading, isUnitsError, getActiveUnitsAndSelectedUnit } = useOrganizationUnits({ organization });
  const organizationUnitsById = getActiveUnitsAndSelectedUnit(rule?.organization_unit_id);

  const isSubmitDisabled = isSubmitting || isUnitsLoading || isUnitsError || isConditionFieldsLoading;

  const theme = useTheme();

  const fetchCoveragesAndIncidentTypes = useCallback(
    async (sub_orgs_ids, lob) => {
      setScopeTypesOptions({ claim: [], exposure: [] });
      setScopeSubTypesOptions({});

      try {
        if (lob !== 'all') {
          const [{ data: supportedCoveragesForSubOrgAndLob }, { data: supportedIncidentTypesForSubOrgAndLobWithData }] =
            await Promise.all([
              axios.get(`/api/v1/organizations/${organization.id}/coverages`, { params: { sub_orgs_ids, lob } }),
              axios.get(`/api/v1/organizations/${organization.id}/incident_types`, { params: { sub_orgs_ids, lob } }),
            ]);

          const supportedIncidentTypesForSubOrgAndLob = _.mapValues(
            supportedIncidentTypesForSubOrgAndLobWithData,
            'desc'
          );
          setScopeTypesOptions({
            claim: Object.keys(supportedIncidentTypesForSubOrgAndLob)
              .map((key) => ({ desc: supportedIncidentTypesForSubOrgAndLob[key], key: `${lob}_${key}` }))
              .concat([ALL_OPTION]),
            exposure: Object.keys(supportedCoveragesForSubOrgAndLob)
              .map((key) => ({ desc: supportedCoveragesForSubOrgAndLob[key], key: `${lob}_${key}` }))
              .concat([ALL_OPTION]),
          });

          // Build per-incident-type sub-types map, keyed by the same `${lob}_${incidentKey}` shape
          // used by scope_types so lookups stay symmetric.
          const subTypesByScopeKey = {};
          Object.entries(supportedIncidentTypesForSubOrgAndLobWithData || {}).forEach(
            ([incidentKey, incidentConfig]) => {
              const subTypes = incidentConfig?.sub_types;
              if (subTypes && Object.keys(subTypes).length) {
                subTypesByScopeKey[`${lob}_${incidentKey}`] = Object.entries(subTypes)
                  .map(([subKey, subConfig]) => ({
                    desc: subConfig.desc,
                    key: `${lob}_${incidentKey}_${subKey}`,
                  }))
                  .concat([ALL_OPTION]);
              }
            }
          );
          setScopeSubTypesOptions(subTypesByScopeKey);
        }
      } catch (error) {
        await reportAxiosError(error);
      }
    },
    [organization]
  );

  useEffect(() => {
    setFilteredEvents(events.filter((event) => event.scope === values.scope));
  }, [values.scope, events]);

  useEffect(() => {
    const selectedEventConfig = events.find((event) => values.event_key && event.key === values.event_key.key);
    setSelectedEventConfig(selectedEventConfig);
    if (selectedEventConfig?.is_time_based) {
      setFieldValue('schedule_immediate_or_custom', 'custom');
    }
    if (selectedEventConfig && selectedEventConfig.has_type) {
      setEventTypeOptions(selectedEventConfig.types.concat(ALL_OPTION));
    } else {
      setEventTypeOptions([]);
    }
  }, [values.event_key, events, setFieldValue]);

  useEffect(() => {
    setRelevantTokens(
      _.pickBy(notificationUsableTokensMap, (token) => {
        const doesMatchScope = token.scope === 'claim' || (values.scope && token.scope === values.scope);
        const doesMatchEvent =
          token.context === 'claim' ||
          token.context === 'exposure' ||
          (values.event_key && values.event_key.context === token.context);
        return doesMatchScope && doesMatchEvent;
      })
    );
  }, [values.scope, values.event_key, notificationUsableTokensMap]);

  const noSpecificLobSelected = !values.lob || values.lob === 'all';
  const noSpecificSubOrgSelected =
    organization.sub_organizations_enabled &&
    (!values.sub_organization_ids.length || values.sub_organization_ids.indexOf('all') > -1);
  const shouldDisableScopeTypesSelection = !values.scope || noSpecificLobSelected || noSpecificSubOrgSelected;

  const selectedScopeTypeKey =
    values.scope_types?.length === 1 && values.scope_types[0]?.key && values.scope_types[0].key !== 'all'
      ? values.scope_types[0].key
      : null;
  const scopeSubTypeOptionsForSelection = selectedScopeTypeKey ? scopeSubTypesOptions[selectedScopeTypeKey] || [] : [];
  const shouldDisableScopeSubTypesSelection =
    shouldDisableScopeTypesSelection ||
    values.scope !== 'claim' ||
    !selectedScopeTypeKey ||
    scopeSubTypeOptionsForSelection.length === 0;

  const getUnitName = (unitId) => {
    return isUnitsError
      ? 'Error loading units'
      : isUnitsLoading
        ? 'Loading...'
        : organizationUnitsById[unitId]?.name || '';
  };

  useAllOptionOnMultiSelect(values.scope_types, 'scope_types', ALL_OPTION);
  useAllOptionOnMultiSelect(values.scope_sub_types, 'scope_sub_types', ALL_OPTION);
  useAllOptionOnMultiSelect(values.event_criteria_types, 'event_criteria_types', ALL_OPTION);
  useAllOptionOnMultiSelect(values.sub_organization_ids, 'sub_organization_ids', 'all');
  useAllOptionOnMultiSelect(values.time_based_directions, 'time_based_directions', 'all');
  useAllOptionOnMultiSelect(values.time_based_channels, 'time_based_channels', 'all');
  useAllOptionOnMultiSelect(values.time_based_category_ids, 'time_based_category_ids', 'all');
  useAllOptionOnMultiSelect(values.time_based_stage_keys, 'time_based_stage_keys', 'all');
  useAllOptionOnMultiSelect(values.time_based_recipient, 'time_based_recipient', 'all');

  // Every scope change that clears the legacy `conditions` rows must clear the
  // QueryBuilder tree too: a tree built against one LOB's / event's field
  // catalog is not valid against another's, and the BE rejects unknown paths.
  useSetDefaultFieldsOnChange(values.scope, {
    conditions: [],
    conditions_json: null,
    title: '',
    content: '',
    scope_types: [ALL_OPTION],
    scope_sub_types: [ALL_OPTION],
    event_key: null,
  });

  useSetDefaultFieldsOnChange(values.scope_types, { scope_sub_types: [ALL_OPTION] });

  useSetDefaultFieldsOnChange(values.event_key, {
    conditions: [],
    conditions_json: null,
    event_criteria_types: [ALL_OPTION],
  });

  useSetDefaultFieldsOnChange(
    values.lob,
    {
      conditions: [],
      conditions_json: null,
      scope_types: [ALL_OPTION],
      scope_sub_types: [ALL_OPTION],
      event_key: null,
    },
    () =>
      fetchCoveragesAndIncidentTypes(
        values.sub_organization_ids.indexOf('all') > -1 ? [] : values.sub_organization_ids,
        values.lob
      )
  );

  // When editing an existing rule, keep the current LOB when sub-orgs change so user can add sub-orgs
  // without being blocked by an unchangeable LOB. For new rules, clearing LOB on sub-org change is fine.
  useSetDefaultFieldsOnChange(values.sub_organization_ids, rule ? {} : { lob: '' });

  useSetDefaultFieldsOnChange(values.recipient, { organization_unit_id: undefined });

  const isConditionsFormikDisabled =
    isSubmitting ||
    (organization.sub_organizations_enabled && _.isEmpty(values.sub_organization_ids)) ||
    !values.lob ||
    !values.scope ||
    !values.scope_status;

  const isTimeBasedNotificationsEnabled = isFeatureEnabled(
    organization,
    CONFIGURATION_FEATURES_NAMES.TIME_BASED_NOTIFICATIONS
  );

  const isQueryBuilderConditionsEnabled = shouldUseConditionsQueryBuilder(
    isFeatureEnabled(organization, CONFIGURATION_FEATURES_NAMES.NOTIFICATION_RULES_QUERY_BUILDER),
    values.rule_trigger_type
  );

  const isAllSubOrgsForConditions =
    values.sub_organization_ids.indexOf('all') > -1 || !organization.sub_organizations_enabled;
  const subOrgIdsForConditions = isAllSubOrgsForConditions ? [] : values.sub_organization_ids;

  const isTimeBased = values.rule_trigger_type === 'time_based';

  // Falls back to the rule's own key. `convertServerRuleToFormikValues` resolves `event_key`
  // against the org's *visible* event list, so a rule whose event is feature-gated off resolves
  // to `undefined` — and gating the conditions builder on that would hide an authored tree behind
  // "Select an Event…" rather than let its owner see it.
  const conditionsEventKey = values.event_key?.key ?? rule?.event_key ?? '';

  const applyTriggerTypeFields = (newType) => {
    setValues((prev) => ({
      ...prev,
      rule_trigger_type: newType,
      is_immediate: true,
      schedule_immediate_or_custom: 'immediate',
      time_based_condition: null,
      conditions: [],
      conditions_json: null,
      title: '',
      content: '',
      time_based_directions: ['all'],
      time_based_channels: ['all'],
      time_based_category_ids: ['all'],
      time_based_stage_keys: ['all'],
      time_based_recipient: ['all'],
      ...(newType === 'time_based' && {
        event_key: null,
        event_criteria_types: [ALL_OPTION],
        scope: 'claim',
        due_date_immediate_or_custom: 'immediate',
        due_date_number: undefined,
        due_date_timeframe: undefined,
      }),
    }));
  };

  const handleRuleTriggerTypeChange = (_id, newType) => {
    const hasUserChanges =
      values.conditions.length > 0 ||
      !isEmptyConditionsTree(values.conditions_json) ||
      values.title ||
      values.content ||
      values.time_based_condition;
    if (hasUserChanges) {
      setPendingTriggerTypeChange(newType);
    } else {
      applyTriggerTypeFields(newType);
    }
  };

  const confirmTriggerTypeChange = () => {
    applyTriggerTypeFields(pendingTriggerTypeChange);
    setPendingTriggerTypeChange(null);
  };

  const cancelTriggerTypeChange = () => {
    setPendingTriggerTypeChange(null);
  };

  return (
    <CardDialog
      isDialog
      title={`${rule ? 'Edit' : 'Add '} Notification`}
      fullWidth
      maxWidth="md"
      onClose={onClose}
      preventClose={isSubmitting}
      footerActions={
        <DialogFooterActions disabled={isSubmitDisabled} onClickPrimary={handleSubmit} onClickSecondary={onClose} />
      }
    >
      <Grid container spacing={2} className={styles.notificationDialogInner}>
        {organization.sub_organizations_enabled && (
          <Grid item xs={12}>
            <AlertBanner
              note="Editing the sub-orgs or LoBs will clear all conditions"
              title="Please Note"
              alertType={AlertBanner.ALERT_TYPES.INFO}
              withIcon
            />
          </Grid>
        )}

        {isTimeBasedNotificationsEnabled && (
          <Grid item xs={12}>
            <Card title="Notification Trigger Type" cardType={Card.CARD_TYPES.PRIMARY}>
              <div className="flex flex-row">
                <RadioButtonFormik
                  id="rule_trigger_type"
                  label="Event Based"
                  optionValue="event_based"
                  onChange={handleRuleTriggerTypeChange}
                  disabled={isSubmitting || !!rule}
                />
                <RadioButtonFormik
                  id="rule_trigger_type"
                  label="Time Based"
                  optionValue="time_based"
                  onChange={handleRuleTriggerTypeChange}
                  disabled={isSubmitting || !!rule}
                />
              </div>
            </Card>
          </Grid>
        )}

        <ConfirmModal
          isOpen={!!pendingTriggerTypeChange}
          onClose={cancelTriggerTypeChange}
          onPrimaryBtnClick={confirmTriggerTypeChange}
          title="Change Notification Trigger Type"
          contentText="Switching the notification trigger type will clear all conditions and notification content. Are you sure you want to continue?"
          primaryButtonName="Continue"
          secondaryButtonName="Cancel"
        />

        <Grid item xs={12}>
          <Card title="Context" cardType={Card.CARD_TYPES.PRIMARY}>
            {organization.sub_organizations_enabled && (
              <div className={styles.subOrgSection}>
                <Text
                  variant={Text.VARIANTS.SM}
                  weight={Text.WEIGHTS.REGULAR}
                  colorVariant={Text.COLOR_VARIANTS.PRIMARY}
                >
                  Select Sub Organization
                </Text>
                <MultiSelectTextFieldFormik
                  id="sub_organization_ids"
                  label="Sub Organizations"
                  disabled={isSubmitting}
                  options={organization.sub_organizations.map((subOrg) => subOrg.id)}
                  renderValue={() => ''}
                  renderOption={(subOrgId) =>
                    subOrgId === 'all' ? 'All (inc. future sub-orgs)' : subOrgIdToName[subOrgId]
                  }
                  className={classes.formTextFieldNoErrorSpacing}
                  withOptionChips
                  fullWidth
                  sortAlphabetic
                  addAllOption
                  allOptionValue="all"
                />
              </div>
            )}
            <Grid item xs={12}>
              <div className={styles.selectLobSection}>
                <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR} className={styles.selectLobTitle}>
                  Select line of business
                </Text>
                <div className="flex">
                  {rule ? (
                    rule.lob && rule.lob !== 'all' ? (
                      <LobCell lob={rule.lob} />
                    ) : (
                      'All'
                    )
                  ) : (
                    <LobRadioFormik
                      lobFieldId="lob"
                      showAllLobsOption
                      subOrganizationIds={
                        values.sub_organization_ids.length
                          ? values.sub_organization_ids
                          : organization.sub_organizations.map((so) => so.id)
                      }
                    />
                  )}
                </div>
              </div>
            </Grid>

            <div className="mb-16">
              {isTimeBased ? <PriorityCategoryFieldsFormikInner /> : <RuleNotificationConfigurationFieldsFormik />}
            </div>
            <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
              Select Entity
            </Text>
            <Grid container direction="row" spacing={3}>
              <Grid item xs={2}>
                <TextFieldFormik
                  id="scope"
                  label="Entity"
                  disabled={isSubmitting || isTimeBased}
                  className={classes.formTextField}
                  fullWidth
                  select
                >
                  {(isTimeBased ? ['claim'] : Object.keys(NOTIFICATIONS_SCOPES)).map((entity) => (
                    <MenuItem key={entity} value={entity}>
                      {NOTIFICATIONS_SCOPES[entity]['desc']}
                    </MenuItem>
                  ))}
                </TextFieldFormik>
              </Grid>
              <Grid item xs={2}>
                <TextFieldFormik
                  id="scope_status"
                  label="Entity Status"
                  disabled={isSubmitting}
                  className={classes.formTextField}
                  fullWidth
                  select
                >
                  {Object.keys(NOTIFICATIONS_ENTITY_STATUSES).map((entity) => (
                    <MenuItem key={entity} value={entity}>
                      {NOTIFICATIONS_ENTITY_STATUSES[entity]['desc']}
                    </MenuItem>
                  ))}
                </TextFieldFormik>
              </Grid>
              <Grid item xs={4}>
                <MultiSelectTextFieldFormik
                  id="scope_types"
                  label="Entity Type"
                  disabled={isSubmitting || shouldDisableScopeTypesSelection}
                  options={scopeTypesOptions[values.scope] || []}
                  renderValue={() => ''}
                  renderOption={(value) => value?.desc || ''}
                  className={classes.formTextFieldNoErrorSpacing}
                  withOptionChips
                  fullWidth
                />
                {shouldDisableScopeTypesSelection && (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <WarningIcon size={24} iconColor={theme.palette.warning.main} />
                    <Typography
                      variant="body2"
                      style={{ fontSize: '14px', marginLeft: '5px', color: '#606060' }}
                    >{`Choose a specific ${
                      organization.sub_organizations_enabled ? 'sub org,' : ''
                    } lob and entity to enable selection`}</Typography>
                  </div>
                )}
              </Grid>
              {values.scope === 'claim' && (
                <Grid item xs={4}>
                  <MultiSelectTextFieldFormik
                    id="scope_sub_types"
                    label="Entity Sub-Type"
                    disabled={isSubmitting || shouldDisableScopeSubTypesSelection}
                    options={scopeSubTypeOptionsForSelection}
                    renderValue={() => ''}
                    renderOption={(value) => value?.desc || ''}
                    className={classes.formTextFieldNoErrorSpacing}
                    withOptionChips
                    fullWidth
                  />
                  {shouldDisableScopeSubTypesSelection && !shouldDisableScopeTypesSelection && (
                    <Typography variant="body2" style={{ fontSize: '14px', color: '#606060' }}>
                      Select a single Entity Type to filter by sub-types
                    </Typography>
                  )}
                </Grid>
              )}
            </Grid>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card title="Details">
            <Grid item xs={12}>
              <div>
                <TextFieldFormik
                  id="rule_name"
                  label="Rule Name"
                  disabled={isSubmitting}
                  className={classes.formTextField}
                  fullWidth
                />
              </div>
            </Grid>
            <Grid item xs={6} />
            <Grid container direction="row" spacing={3}>
              <Grid item xs={4}>
                <TextFieldFormik
                  id="recipient"
                  label="Recipient"
                  disabled={isSubmitting}
                  className={classes.formTextField}
                  fullWidth
                  select
                >
                  {Object.keys(recipientTypes)
                    .filter(
                      (recipient) =>
                        !recipientTypes[recipient].scope || recipientTypes[recipient].scope === values.scope
                    )
                    .map((entity) => (
                      <MenuItem key={entity} value={entity}>
                        {recipientTypes[entity]['desc']}
                      </MenuItem>
                    ))}
                </TextFieldFormik>
              </Grid>
              <Grid item xs={4}>
                {values.recipient === 'organization_unit_leader' && (
                  <AutocompleteFormik
                    id="organization_unit_id"
                    label="Organization Unit"
                    options={Object.keys(organizationUnitsById)}
                    getOptionLabel={getUnitName}
                    showOnly={isUnitsLoading || isUnitsError}
                    sortAlphabetic
                    disabled={isSubmitting || isUnitsLoading || isUnitsError}
                    className={classes.formTextField}
                  />
                )}
              </Grid>
            </Grid>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card title="Conditions" cardType={Card.CARD_TYPES.SECONDARY}>
            {/* Time-Based Condition UI */}
            {isTimeBased ? (
              <TimeBasedConditionFormik disabled={isSubmitting} />
            ) : (
              <>
                {/* Event-Based Condition UI (existing) */}
                <div className={cn('mb-12 flex flex-grow flex-row space-x-16')}>
                  <Grid item xs={4} className={cn('mb-12')}>
                    <AutocompleteFormik
                      id="event_key"
                      label="Event"
                      options={filteredEvents}
                      getOptionLabel={(event) => event.desc}
                      sortAlphabetic
                      disabled={isSubmitting}
                      className={classes.formTextField}
                    />
                  </Grid>
                  {eventTypeOptions.length > 0 && (
                    <Grid item xs={3}>
                      <MultiSelectTextFieldFormik
                        id="event_criteria_types"
                        label="Type"
                        disabled={isSubmitting}
                        options={eventTypeOptions}
                        renderValue={(value) => value.key}
                        renderOption={(value) => value.desc}
                        className={classes.formTextFieldNoErrorSpacing}
                        fullWidth
                        withOptionChips
                        sortAlphabetic
                      />
                    </Grid>
                  )}
                </div>
                <div className={cn('mb-24 flex flex-grow flex-row gap-16')}>
                  <EventScheduleSelect selectedEventConfig={selectedEventConfig} isSubmitting={isSubmitting} />
                </div>
                {isQueryBuilderConditionsEnabled ? (
                  <NotificationConditionsQueryBuilderFormik
                    eventKey={conditionsEventKey}
                    isImmediate={values.schedule_immediate_or_custom === 'immediate'}
                    lob={values.lob}
                    isAllSubOrgs={isAllSubOrgsForConditions}
                    subOrgIds={subOrgIdsForConditions}
                    onLoadingChange={setIsConditionFieldsLoading}
                    onFieldsLoaded={onConditionFieldsLoaded}
                  />
                ) : (
                  <ConditionsFormik conditions={conditions} disabled={isConditionsFormikDisabled} />
                )}
              </>
            )}
            <Grid item xs={12}>
              <TextAreaWithTokens
                id="title"
                label="Notification Title"
                disabled={isSubmitting}
                charsLimit={100}
                tokensList={relevantTokens}
                onAddToken={() =>
                  mixpanel.track(NOTIFICATION_MIXPANEL_EVENTS.ADD_NOTIFICATION_TOKEN_CLICKED, {
                    source: NOTIFICATION_MIXPANEL_EVENT_SOURCES.NOTIFICATIONS,
                  })
                }
                onChooseToken={(tokenKey) => {
                  mixpanel.track(NOTIFICATION_MIXPANEL_EVENTS.NOTIFICATION_TOKEN_VALUE_CHOSEN, {
                    source: NOTIFICATION_MIXPANEL_EVENT_SOURCES.NOTIFICATIONS,
                    value: tokenKey,
                  });
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextAreaWithTokens
                id="content"
                label="Notification Text"
                disabled={isSubmitting}
                charsLimit={250}
                tokensList={relevantTokens}
                onAddToken={() =>
                  mixpanel.track(NOTIFICATION_MIXPANEL_EVENTS.ADD_NOTIFICATION_TOKEN_CLICKED, {
                    source: NOTIFICATION_MIXPANEL_EVENT_SOURCES.NOTIFICATIONS,
                  })
                }
                onChooseToken={(tokenKey) => {
                  mixpanel.track(NOTIFICATION_MIXPANEL_EVENTS.NOTIFICATION_TOKEN_VALUE_CHOSEN, {
                    source: NOTIFICATION_MIXPANEL_EVENT_SOURCES.NOTIFICATIONS,
                    value: tokenKey,
                  });
                }}
              />
            </Grid>
          </Card>
        </Grid>
      </Grid>
    </CardDialog>
  );
};

NotificationDialogInner.propTypes = {
  rule: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  subOrgIdToName: PropTypes.object.isRequired,
  events: PropTypes.array.isRequired,
  conditions: PropTypes.object.isRequired,
  onConditionFieldsLoaded: PropTypes.func.isRequired,
};
